import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { CURRENCIES, VIP_TIERS } from '../data/catalog';
import api from '../services/api';

const AppContext = createContext(null);

const STORAGE_KEY = 'nexus.state.v1';

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persisted = loadState() || {};

const initialState = {
  cart: persisted.cart || [],
  user: persisted.user || null,
  wishlist: persisted.wishlist || [],
  orders: persisted.orders || [],
  appliedCoupon: persisted.appliedCoupon || null,
  recentViews: persisted.recentViews || [],
};

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const key = `${action.item.productId}-${action.item.denomKey}`;
      const idx = state.cart.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...state.cart];
        next[idx] = { ...next[idx], qty: next[idx].qty + action.item.qty };
        return { ...state, cart: next };
      }
      return { ...state, cart: [...state.cart, { ...action.item, key }] };
    }
    case 'UPDATE_QTY': {
      return {
        ...state,
        cart: state.cart
          .map((i) => (i.key === action.key ? { ...i, qty: action.qty } : i))
          .filter((i) => i.qty > 0),
      };
    }
    case 'REMOVE':
      return { ...state, cart: state.cart.filter((i) => i.key !== action.key) };
    case 'CLEAR':
      return { ...state, cart: [], appliedCoupon: null };
    case 'TOGGLE_WISH': {
      const inList = state.wishlist.includes(action.id);
      return {
        ...state,
        wishlist: inList
          ? state.wishlist.filter((x) => x !== action.id)
          : [...state.wishlist, action.id],
      };
    }
    case 'LOGIN':
      return { ...state, user: action.user };
    case 'LOGOUT':
      return { ...state, user: null };
    case 'APPLY_COUPON':
      return { ...state, appliedCoupon: action.coupon };
    case 'REMOVE_COUPON':
      return { ...state, appliedCoupon: null };
    case 'PLACE_ORDER':
      return {
        ...state,
        orders: [action.order, ...state.orders],
        cart: [],
        appliedCoupon: null,
      };
    case 'PUSH_RECENT': {
      const id = action.id;
      if (!id) return state;
      const filtered = state.recentViews.filter((x) => x !== id);
      return { ...state, recentViews: [id, ...filtered].slice(0, 10) };
    }
    case 'CLEAR_RECENT':
      return { ...state, recentViews: [] };
    default:
      return state;
  }
}

export const COUPONS = {
  QQCARD5: { code: 'QQCARD5', off: 0.05, label: '新會員 5% 折扣' },
  HOT10: { code: 'HOT10', off: 0.1, label: '熱銷 10% 折扣 (限 500 元以上)', min: 500 },
  VIP15: { code: 'VIP15', off: 0.15, label: 'VIP 限定 15%' },
  WELCOME: { code: 'WELCOME', off: 50, type: 'fixed', label: '迎新 -NT$50' },
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [lang, setLang] = useState(persisted.lang || 'zh-TW');
  const [theme, setTheme] = useState(persisted.theme || 'light');
  const [currency, setCurrency] = useState(persisted.currency || 'TWD');
  const [route, setRoute] = useState({ page: 'home', params: {} });
  const [drawer, setDrawer] = useState({ open: null }); // 'cart' | 'auth' | 'account' | 'search' | 'chat' | null
  const [toasts, setToasts] = useState([]);

  // Persist
  useEffect(() => {
    const payload = {
      cart: state.cart,
      user: state.user,
      wishlist: state.wishlist,
      orders: state.orders,
      appliedCoupon: state.appliedCoupon,
      recentViews: state.recentViews,
      lang,
      theme,
      currency,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }, [state, lang, theme, currency]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.lang = lang;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0b0c10' : '#fafafb');
  }, [theme, lang]);

  const navigate = useCallback((page, params = {}) => {
    setRoute({ page, params });
    if (page === 'product' && params?.id) {
      dispatch({ type: 'PUSH_RECENT', id: params.id });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const toast = useCallback((msg, kind = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 3500);
  }, []);

  const addToCart = useCallback(
    (item, productName) => {
      dispatch({ type: 'ADD', item });
      toast(`✓ ${productName || ''} 已加入購物車`, 'success');
    },
    [toast]
  );

  const fx = useCallback(
    (twd) => {
      const c = CURRENCIES[currency];
      const value = Math.round(twd * c.rate * 100) / 100;
      const formatted =
        currency === 'JPY' || currency === 'KRW'
          ? Math.round(value).toLocaleString()
          : value.toLocaleString(undefined, {
              minimumFractionDigits: currency === 'TWD' ? 0 : 2,
              maximumFractionDigits: 2,
            });
      return `${c.symbol}${formatted}`;
    },
    [currency]
  );

  const cartSubtotal = useMemo(
    () =>
      state.cart.reduce((sum, it) => sum + it.price * it.qty, 0),
    [state.cart]
  );

  const vipTier = useMemo(() => {
    const spent = state.user?.lifetimeSpent || 0;
    return [...VIP_TIERS].reverse().find((t) => spent >= t.threshold) || VIP_TIERS[0];
  }, [state.user]);

  const nextTier = useMemo(() => {
    const spent = state.user?.lifetimeSpent || 0;
    return VIP_TIERS.find((t) => spent < t.threshold) || null;
  }, [state.user]);

  const couponDiscount = useMemo(() => {
    if (!state.appliedCoupon) return 0;
    const c = COUPONS[state.appliedCoupon];
    if (!c) return 0;
    if (c.min && cartSubtotal < c.min) return 0;
    if (c.type === 'fixed') return c.off;
    return Math.round(cartSubtotal * c.off);
  }, [state.appliedCoupon, cartSubtotal]);

  const vipRebate = useMemo(() => {
    if (!state.user) return 0;
    const rate = parseFloat(vipTier.rebate) / 100;
    return Math.round((cartSubtotal - couponDiscount) * rate);
  }, [state.user, vipTier, cartSubtotal, couponDiscount]);

  const cartTotal = useMemo(
    () => Math.max(0, cartSubtotal - couponDiscount),
    [cartSubtotal, couponDiscount]
  );

  const cartCount = useMemo(
    () => state.cart.reduce((s, i) => s + i.qty, 0),
    [state.cart]
  );

  const login = useCallback(
    async (email, password = '123456') => {
      try {
        const { user } = await api.signIn({ email, password });
        dispatch({ type: 'LOGIN', user });
        toast(`歡迎回來，${user.name}！`, 'success');
        setDrawer({ open: null });
        return user;
      } catch (e) {
        const messages = {
          INVALID_EMAIL: '請輸入有效的 Email',
          WEAK_PASSWORD: '密碼至少需 6 個字元',
          RATE_LIMIT: '請稍候再試',
        };
        toast(messages[e.message] || '登入失敗，請稍候再試', 'error');
        throw e;
      }
    },
    [toast]
  );

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
    toast('已登出', 'info');
  }, [toast]);

  const applyCoupon = useCallback(
    (code) => {
      const upper = code.trim().toUpperCase();
      const c = COUPONS[upper];
      if (!c) {
        toast('此優惠碼無效', 'error');
        return false;
      }
      if (c.min && cartSubtotal < c.min) {
        toast(`本碼需消費滿 NT$${c.min}`, 'error');
        return false;
      }
      dispatch({ type: 'APPLY_COUPON', coupon: upper });
      toast(`✓ 已套用「${c.label}」`, 'success');
      return true;
    },
    [cartSubtotal, toast]
  );

  const placeOrder = useCallback(
    async (paymentMethod, contact = {}) => {
      try {
        const result = await api.placeOrder({
          items: state.cart,
          paymentMethod,
          contact,
        });
        const order = {
          id: result.id,
          items: state.cart,
          total: cartTotal,
          subtotal: cartSubtotal,
          discount: couponDiscount,
          rebate: vipRebate,
          paymentMethod,
          createdAt: result.paidAt,
          status: 'delivered',
          codes: result.items.map((it) => ({
            name: it.name,
            serial: it.serial,
          })),
        };
        dispatch({ type: 'PLACE_ORDER', order });
        return order;
      } catch (e) {
        toast('付款處理失敗，請稍候再試', 'error');
        throw e;
      }
    },
    [state.cart, cartTotal, cartSubtotal, couponDiscount, vipRebate, toast]
  );

  const subscribeNewsletter = useCallback(
    async (email) => {
      try {
        await api.subscribeNewsletter(email);
        toast(`已訂閱 ${email}`, 'success');
        return true;
      } catch (e) {
        const messages = {
          INVALID_EMAIL: '請輸入有效的 Email',
          RATE_LIMIT: '請稍後再試',
        };
        toast(messages[e.message] || '訂閱失敗', 'error');
        return false;
      }
    },
    [toast]
  );

  const value = {
    ...state,
    dispatch,
    lang,
    setLang,
    theme,
    setTheme,
    toggleTheme: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')),
    currency,
    setCurrency,
    fx,
    route,
    navigate,
    drawer,
    setDrawer,
    openDrawer: (name) => setDrawer({ open: name }),
    closeDrawer: () => setDrawer({ open: null }),
    toasts,
    toast,
    addToCart,
    cartSubtotal,
    cartTotal,
    cartCount,
    couponDiscount,
    vipRebate,
    vipTier,
    nextTier,
    login,
    logout,
    applyCoupon,
    placeOrder,
    subscribeNewsletter,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
